import ExifReader from "exifreader";

// Exported functions: readUploadedPhotoMetadata(), extractPhotoMetadata(),
// photoMetadataCapturedAt(), photoMetadataLatitude(), photoMetadataLongitude(),
// photoMetadataAltitude(), photoMetadataAccuracy().

const EXIF_READER_OPTIONS = {
  expanded: true,
  computed: true,
  async: true,
  excludeTags: {
    icc: true,
    makerNotes: true,
    mpf: true,
    thumbnail: true,
  },
};

// Extracts and normalizes uploaded photo metadata for gauge reading forms.
// Called by gauge_capture_controller.js before previewing an uploaded photo.
// Example return value: { metadata: { gps: { latitude: 1 } }, values: { latitude: 1 } }.
export async function readUploadedPhotoMetadata(file) {
  const metadata = await extractPhotoMetadata(file);

  return {
    metadata,
    values: uploadedPhotoMetadataValues(metadata),
  };
}

// Reads EXIF/XMP/IPTC metadata from a browser File and returns compact JSON.
// Called by readUploadedPhotoMetadata() before form-specific values are derived.
// Example return value: { file: { name: "photo.jpg" }, gps: { latitude: 39.1 } }.
export async function extractPhotoMetadata(file) {
  const extractedAt = new Date().toISOString();
  let tags = {};

  try {
    // ExifReader returns grouped tag objects. Missing metadata is expected for
    // screenshots, edited images, or browser-stripped uploads, so failures fall
    // back to file-level metadata instead of blocking the upload flow.
    tags = await ExifReader.load(file, EXIF_READER_OPTIONS);
  } catch (error) {
    console.error("Unable to extract photo metadata: " + error);
  }

  return compactObject({
    file: compactObject({
      name: file.name,
      type: file.type,
      size: file.size,
      last_modified_at: isoString(file.lastModified ? new Date(file.lastModified) : null),
    }),
    camera: compactObject({
      make: tagText(tags, ["exif"], ["Make"]),
      model: tagText(tags, ["exif"], ["Model"]),
      software: tagText(tags, ["exif"], ["Software"]),
      lens_model: tagText(tags, ["exif", "xmp"], ["LensModel", "Lens"]),
    }),
    image: compactObject({
      width: tagNumber(tags, ["exif", "file", "png", "riff", "gif"], ["PixelXDimension", "ImageWidth", "Image Width"]),
      height: tagNumber(tags, ["exif", "file", "png", "riff", "gif"], ["PixelYDimension", "ImageLength", "ImageHeight", "Image Height"]),
      orientation: tagValue(tags, ["exif", "xmp"], ["Orientation"]),
      color_space: tagDescription(tags, ["exif"], ["ColorSpace"]) || tagValue(tags, ["exif"], ["ColorSpace"]),
    }),
    timestamps: compactObject({
      captured_at: isoString(tagValue(tags, ["exif", "xmp", "iptc"], ["DateTimeOriginal", "CreateDate", "DateCreated"])),
      digitized_at: isoString(tagValue(tags, ["exif", "xmp"], ["DateTimeDigitized", "DateCreated"])),
      modified_at: isoString(tagValue(tags, ["exif", "xmp", "png"], ["DateTime", "ModifyDate", "Modify Date"])),
      file_last_modified_at: isoString(file.lastModified ? new Date(file.lastModified) : null),
      extracted_at: extractedAt,
    }),
    gps: compactObject({
      latitude: gpsNumber(tags, "Latitude"),
      longitude: gpsNumber(tags, "Longitude"),
      altitude: gpsNumber(tags, "Altitude"),
      accuracy: tagNumber(tags, ["exif"], ["GPSHPositioningError"]),
      speed: tagNumber(tags, ["exif"], ["GPSSpeed"]),
      direction: tagNumber(tags, ["exif"], ["GPSImgDirection", "GPSTrack"]),
    }),
    exposure: compactObject({
      iso: tagNumber(tags, ["exif"], ["ISOSpeedRatings", "ISO"]),
      f_number: tagNumber(tags, ["exif"], ["FNumber"]),
      exposure_time: tagNumber(tags, ["exif"], ["ExposureTime"]),
      focal_length: tagNumber(tags, ["exif"], ["FocalLength"]),
    }),
  });
}

// Returns the best capture timestamp available in normalized metadata.
// Called by uploadedPhotoMetadataValues() for the hidden photo_captured_at field.
// Example return value: "2026-06-28T14:03:00.000Z".
export function photoMetadataCapturedAt(metadata) {
  return metadata?.timestamps?.captured_at || metadata?.timestamps?.file_last_modified_at || "";
}

// Returns the GPS latitude when present.
// Called by uploadedPhotoMetadataValues() for gauge reading location state.
// Example return value: 39.7392.
export function photoMetadataLatitude(metadata) {
  return metadata?.gps?.latitude ?? "";
}

// Returns the GPS longitude when present.
// Called by uploadedPhotoMetadataValues() for gauge reading location state.
// Example return value: -104.9903.
export function photoMetadataLongitude(metadata) {
  return metadata?.gps?.longitude ?? "";
}

// Returns the GPS altitude when present.
// Called by uploadedPhotoMetadataValues() for gauge reading location state.
// Example return value: 1609.3.
export function photoMetadataAltitude(metadata) {
  return metadata?.gps?.altitude ?? "";
}

// Returns the GPS horizontal accuracy when present.
// Called by uploadedPhotoMetadataValues() for gauge reading location state.
// Example return value: 5.
export function photoMetadataAccuracy(metadata) {
  return metadata?.gps?.accuracy ?? "";
}

// Maps normalized metadata into the exact values the controller stores.
// Called by readUploadedPhotoMetadata() after EXIF/XMP parsing completes.
// Example return value: { capturedAt: "", latitude: "", longitude: "" }.
function uploadedPhotoMetadataValues(metadata) {
  return {
    capturedAt: photoMetadataCapturedAt(metadata),
    latitude: photoMetadataLatitude(metadata),
    longitude: photoMetadataLongitude(metadata),
    accuracy: photoMetadataAccuracy(metadata),
    altitude: photoMetadataAltitude(metadata),
  };
}

// Returns ExifReader's computed GPS value for one coordinate-like field.
// Called by extractPhotoMetadata() when building normalized gps metadata.
// Example return value: -97.7431.
function gpsNumber(tags, name) {
  const value = tags?.gps?.[name];

  return typeof value === "number" ? value : "";
}

// Finds the first matching ExifReader tag value from the requested groups.
// Called by extractPhotoMetadata() to avoid coupling fields to one metadata group.
// Example return value: "Apple" or 4032.
function tagValue(tags, groups, names) {
  const tag = firstTag(tags, groups, names);

  if (!tag) return "";
  if (tag.computed !== undefined && tag.computed !== null) return normalizeTagValue(tag.computed);
  if (tag.value !== undefined && tag.value !== null) return normalizeTagValue(tag.value);

  return normalizeTagValue(tag.description);
}

// Finds a matching tag and coerces the value into text.
// Called by extractPhotoMetadata() for camera and lens display metadata.
// Example return value: "iPhone 15 Pro".
function tagText(tags, groups, names) {
  const value = tagValue(tags, groups, names);

  return value === "" ? "" : String(value);
}

// Finds a matching tag and coerces the value into a finite number.
// Called by extractPhotoMetadata() for dimensions, GPS detail, and exposure values.
// Example return value: 12.5.
function tagNumber(tags, groups, names) {
  const tag = firstTag(tags, groups, names);

  return numericTagValue(tag);
}

// Finds the first human-readable description for a matching tag.
// Called by extractPhotoMetadata() for fields where ExifReader maps numeric codes.
// Example return value: "sRGB".
function tagDescription(tags, groups, names) {
  const tag = firstTag(tags, groups, names);

  return normalizeTagValue(tag?.description);
}

// Searches grouped ExifReader tags using the supplied group and tag-name order.
// Called by tagValue(), tagNumber(), and tagDescription().
// Example return value: { value: "Apple", description: "Apple" }.
function firstTag(tags, groups, names) {
  for (const group of groups) {
    for (const name of names) {
      const tag = tags?.[group]?.[name];

      if (tag !== undefined && tag !== null) return tag;
    }
  }

  return null;
}

// Converts ExifReader tag payloads into plain JSON-friendly values.
// Called by tagValue() and tagDescription() before metadata is submitted.
// Example return value: "Apple" for ["Apple"].
function normalizeTagValue(value) {
  if (value === undefined || value === null) return "";
  if (Array.isArray(value) && value.length === 1) return normalizeTagValue(value[0]);
  if (Array.isArray(value)) return normalizeTagValueList(value);

  return value;
}

// Normalizes every entry in an ExifReader array payload.
// Called by normalizeTagValue() for multi-value metadata tags.
// Example return value: ["N", "W"].
function normalizeTagValueList(values) {
  return values.map(normalizeTagValue);
}

// Converts numeric, rational, and description-backed ExifReader tags to numbers.
// Called by tagNumber() for tags whose raw storage can vary by camera.
// Example return value: 0.005 for an exposure time tag.
function numericTagValue(tag) {
  const value = normalizeTagValue(tag?.computed ?? tag?.value);

  if (typeof value === "number") return Number.isFinite(value) ? value : "";
  if (Array.isArray(value) && value.length === 2 && allNumbers(value)) {
    return value[1] === 0 ? "" : value[0] / value[1];
  }

  return numericDescriptionValue(tag?.description);
}

// Checks that every array entry is numeric.
// Called by numericTagValue() before dividing rational-style arrays.
// Example return value: true for [1, 200].
function allNumbers(values) {
  return values.every(numberValue);
}

// Checks whether a single value is a number.
// Called by allNumbers() as the Array.every predicate.
// Example return value: true for 35.
function numberValue(value) {
  return typeof value === "number";
}

// Parses ExifReader descriptions when the raw tag is not already numeric.
// Called by numericTagValue() as a final fallback.
// Example return value: 35 for "35 mm".
function numericDescriptionValue(value) {
  if (value === undefined || value === null || value === "") return "";

  const parsed = Number.parseFloat(String(value));

  return Number.isFinite(parsed) ? parsed : "";
}

// Converts Date objects, ISO strings, and EXIF timestamp strings to ISO.
// Called by extractPhotoMetadata() for capture and modification timestamps.
// Example return value: "2026-06-28T14:03:00.000Z".
function isoString(value) {
  if (!value) return "";

  const normalized = normalizeTagValue(value);
  const date = normalized instanceof Date ? normalized : new Date(exifDateString(normalized));

  return Number.isNaN(date.getTime()) ? "" : date.toISOString();
}

// Converts common EXIF date strings into a format JavaScript can parse.
// Called by isoString() before constructing a Date.
// Example return value: "2026-06-28T14:03:00".
function exifDateString(value) {
  const stringValue = String(value);
  const match = stringValue.match(/^(\d{4}):(\d{2}):(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(.*)$/);

  if (!match) return stringValue;

  const [, year, month, day, hour, minute, second, suffix] = match;

  return `${year}-${month}-${day}T${hour}:${minute}:${second}${suffix || ""}`;
}

// Removes blank values and empty sections from normalized metadata.
// Called by extractPhotoMetadata() for the root object and each nested section.
// Example return value: { file: { name: "photo.jpg" } }.
function compactObject(object) {
  return Object.fromEntries(Object.entries(object).filter(metadataEntryHasValue));
}

// Decides whether a normalized metadata entry should be retained.
// Called by compactObject() while pruning blank fields and sections.
// Example return value: false for ["gps", {}].
function metadataEntryHasValue(entry) {
  const [, value] = entry;

  if (value === undefined || value === null || value === "") return false;
  if (typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0) return false;

  return true;
}
